// components/SupportChatbot.tsx
'use client';

import { useState, useRef, useEffect, useCallback, memo, FormEvent, ChangeEvent, ClipboardEvent } from 'react';
import { X, Minus, Send, MessageCircle, ImagePlus, SquarePen, ChevronLeft, CheckCircle2, Circle } from '@/components/ui/lucide-memo';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// ── Types ──

interface MessageContent {
  type: 'text' | 'image_url';
  text?: string;
  image_url?: { url: string };
}

interface Message {
  role: 'user' | 'assistant';
  content: string | MessageContent[];
}

interface Ticket {
  id: string;
  title: string;
  messages: Message[];
  resolved: boolean;
  createdAt: string;
}

interface PendingImage {
  id: string;
  url: string;
}

// ── Helpers ──

function getTextContent(msg: Message): string {
  if (typeof msg.content === 'string') return msg.content;
  return msg.content.filter((p) => p.type === 'text').map((p) => p.text || '').join('');
}

function getImageUrls(msg: Message): string[] {
  if (typeof msg.content === 'string') return [];
  return msg.content.filter((p) => p.type === 'image_url' && p.image_url?.url).map((p) => p.image_url!.url);
}

function deriveTitle(messages: Message[]): string {
  const first = messages.find((m) => m.role === 'user');
  if (!first) return 'New conversation';
  const text = getTextContent(first);
  return text.length > 60 ? text.slice(0, 57) + '...' : text || 'New conversation';
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const GREETING_MESSAGE: Message = {
  role: 'assistant',
  content:
    "Hi there! I'm Fater's support assistant. I can help you with questions about the platform, troubleshoot issues, or create a support ticket if needed. How can I help you today?",
};

const STORAGE_KEY_TICKETS = 'fater-support-tickets';
const STORAGE_KEY_ACTIVE = 'fater-support-active-ticket';

function loadTickets(): Ticket[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_TICKETS);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveTickets(tickets: Ticket[]) {
  try {
    localStorage.setItem(STORAGE_KEY_TICKETS, JSON.stringify(tickets));
  } catch { /* noop */ }
}

// ── Stable JSX constants ──

const PANEL_STYLE = { height: '500px' };
const REMARK_PLUGINS = [remarkGfm];

// ── Repeated list items ──

const MessageBubble = memo(function MessageBubble({ message }: { message: Message }) {
  const text = getTextContent(message);
  const images = getImageUrls(message);
  const isTicketCreated = message.role === 'assistant' && text.includes('[TICKET_CREATED]');
  const isResolved = message.role === 'assistant' && text.includes('[TICKET_RESOLVED]');
  const displayContent = text.replace('[TICKET_CREATED]', '').replace('[TICKET_RESOLVED]', '').trim();

  return (
    <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
          message.role === 'user'
            ? 'bg-[#B3D9FF]/15 text-white/90 border border-[#B3D9FF]/10'
            : 'bg-[#242424] text-white/70'
        }`}
      >
        {images.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {/* Attachments of a sent message are immutable: no insert, removal or reorder is possible. */}
            {images.map((url, j) => (
              <img key={j} src={url} alt="Attached" className="max-h-32 max-w-full rounded-lg object-cover" />
            ))}
          </div>
        )}
        {displayContent && (
          message.role === 'assistant' ? (
            <div className="support-chat-markdown">
              <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{displayContent}</ReactMarkdown>
            </div>
          ) : (
            <p className="whitespace-pre-wrap">{displayContent}</p>
          )
        )}
        {isTicketCreated && (
          <div className="mt-2.5 rounded-xl border border-[#B3D9FF]/20 bg-[#B3D9FF]/10 px-3 py-2.5">
            <div className="flex items-center gap-2 text-[#B3D9FF]">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs font-normal">Support ticket created</span>
            </div>
            <p className="mt-1 text-[11px] text-white/35">
              Our team has been notified and will follow up via email.
            </p>
          </div>
        )}
        {isResolved && (
          <div className="mt-2.5 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-3 py-2.5">
            <div className="flex items-center gap-2 text-emerald-400">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-xs font-normal">Issue resolved</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

const TicketRow = memo(function TicketRow({
  ticket,
  isActive,
  onOpen,
}: {
  ticket: Ticket;
  isActive: boolean;
  onOpen: (ticket: Ticket) => void;
}) {
  const handleClick = useCallback(() => onOpen(ticket), [onOpen, ticket]);

  return (
    <button
      onClick={handleClick}
      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-white/[0.04] ${
        isActive ? 'bg-white/[0.03]' : ''
      }`}
    >
      {ticket.resolved ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400/70" />
      ) : (
        <Circle className="mt-0.5 h-4 w-4 shrink-0 text-white/20" />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] text-white/80">{ticket.title}</p>
        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-white/30">
          <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
          {ticket.resolved && (
            <>
              <span className="text-white/15">·</span>
              <span className="text-emerald-400/60">Resolved</span>
            </>
          )}
        </div>
      </div>
    </button>
  );
});

const PendingImagePreview = memo(function PendingImagePreview({
  image,
  onRemove,
}: {
  image: PendingImage;
  onRemove: (id: string) => void;
}) {
  const handleRemove = useCallback(() => onRemove(image.id), [onRemove, image.id]);

  return (
    <div className="relative shrink-0">
      <img src={image.url} alt="Preview" className="h-14 w-14 rounded-lg object-cover" />
      <button
        type="button"
        onClick={handleRemove}
        className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#1A1A1A] border border-[#2A2A2A] text-white/50 hover:text-white"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </div>
  );
});

// ── Component ──

type View = 'chat' | 'list';

interface SupportChatbotProps {
  onClose?: () => void;
}

export default function SupportChatbot({ onClose }: SupportChatbotProps) {
  const [view, setView] = useState<View>('chat');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([GREETING_MESSAGE]);
  const [input, setInput] = useState('');
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load state from localStorage
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing in this file, inherited by the merge gate (it judges whole files); changing it alters behaviour outside this merge
    setTickets(loadTickets());
    try {
      const activeId = localStorage.getItem(STORAGE_KEY_ACTIVE);
      if (activeId) {
        const all = loadTickets();
        const ticket = all.find((t) => t.id === activeId);
        if (ticket) {
          setActiveTicketId(activeId);
          setMessages(ticket.messages);
        }
      }
    } catch { /* noop */ }
  }, []);

  // Auto-save active conversation to its ticket
  useEffect(() => {
    if (!activeTicketId) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing in this file, inherited by the merge gate (it judges whole files); changing it alters behaviour outside this merge
    setTickets((prev) => {
      const updated = prev.map((t) =>
        t.id === activeTicketId ? { ...t, messages, title: deriveTitle(messages) } : t,
      );
      saveTickets(updated);
      return updated;
    });
  }, [messages, activeTicketId]);

  // Persist active ticket id
  useEffect(() => {
    try {
      if (activeTicketId) localStorage.setItem(STORAGE_KEY_ACTIVE, activeTicketId);
      else localStorage.removeItem(STORAGE_KEY_ACTIVE);
    } catch { /* noop */ }
  }, [activeTicketId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    if (view === 'chat') setTimeout(() => inputRef.current?.focus(), 200);
  }, [view]);

  // ── Image handling ──

  const addImages = useCallback(async (files: File[]) => {
    const urls = await Promise.all(files.filter((f) => f.type.startsWith('image/')).map(fileToDataUrl));
    setPendingImages((prev) => [
      ...prev,
      ...urls.map((url) => ({ id: `img-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, url })),
    ]);
  }, []);

  const removePendingImage = useCallback((id: string) => {
    setPendingImages((prev) => prev.filter((img) => img.id !== id));
  }, []);

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      const files = Array.from(e.clipboardData.files);
      if (files.length > 0) { e.preventDefault(); void addImages(files); }
    },
    [addImages],
  );

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) void addImages(files);
      e.target.value = '';
    },
    [addImages],
  );

  // ── Ticket / conversation management ──

  const ensureTicket = useCallback((): string => {
    if (activeTicketId) return activeTicketId;
    const id = `ticket-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const ticket: Ticket = {
      id,
      title: 'New conversation',
      messages: [GREETING_MESSAGE],
      resolved: false,
      createdAt: new Date().toISOString(),
    };
    setActiveTicketId(id);
    setTickets((prev) => {
      const updated = [ticket, ...prev];
      saveTickets(updated);
      return updated;
    });
    return id;
  }, [activeTicketId]);

  const newConversation = useCallback(() => {
    const id = `ticket-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const ticket: Ticket = {
      id,
      title: 'New conversation',
      messages: [GREETING_MESSAGE],
      resolved: false,
      createdAt: new Date().toISOString(),
    };
    setActiveTicketId(id);
    setMessages([GREETING_MESSAGE]);
    setInput('');
    setPendingImages([]);
    setIsLoading(false);
    setView('chat');
    setTickets((prev) => {
      const updated = [ticket, ...prev];
      saveTickets(updated);
      return updated;
    });
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const openTicket = useCallback((ticket: Ticket) => {
    setActiveTicketId(ticket.id);
    setMessages(ticket.messages);
    setInput('');
    setPendingImages([]);
    setView('chat');
  }, []);

  // ── Send message ──

  const sendMessage = useCallback(
    async (e?: FormEvent) => {
      e?.preventDefault();
      const trimmed = input.trim();
      if ((!trimmed && pendingImages.length === 0) || isLoading) return;

      ensureTicket();

      let userContent: string | MessageContent[];
      if (pendingImages.length > 0) {
        const parts: MessageContent[] = [];
        if (trimmed) parts.push({ type: 'text', text: trimmed });
        for (const img of pendingImages) parts.push({ type: 'image_url', image_url: { url: img.url } });
        userContent = parts;
      } else {
        userContent = trimmed;
      }

      const userMessage: Message = { role: 'user', content: userContent };
      const updatedMessages = [...messages, userMessage];
      setMessages(updatedMessages);
      setInput('');
      setPendingImages([]);
      setIsLoading(true);

      try {
        const res = await fetch('/api/support-chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages: updatedMessages }),
        });

        if (!res.ok) throw new Error('Failed to get response');

        const data = await res.json();
        const content: string = data.content || 'Sorry, I had trouble processing that. Please try again.';
        const assistantMessage: Message = { role: 'assistant', content };
        setMessages((prev) => [...prev, assistantMessage]);

        // Mark resolved if the agent says so
        if (content.includes('[TICKET_RESOLVED]')) {
          setTickets((prev) => {
            const updated = prev.map((t) =>
              t.id === activeTicketId ? { ...t, resolved: true } : t,
            );
            saveTickets(updated);
            return updated;
          });
        }
      } catch {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: 'Sorry, something went wrong. Please try again or contact support@fater.ai directly.' },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [input, pendingImages, isLoading, messages, ensureTicket, activeTicketId],
  );

  const close = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const showList = useCallback(() => setView('list'), []);

  const openFilePicker = useCallback(() => fileInputRef.current?.click(), []);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => setInput(e.target.value), []);

  // ── Render ──

  return (
    <>
      {/* Chat panel, fixed bottom-right */}
      <div
        className="fixed bottom-6 right-6 z-[9999] flex w-[380px] flex-col rounded-[24px] border border-white/[0.06] bg-[#303030] shadow-2xl"
        style={PANEL_STYLE}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#2A2A2A] px-4 py-3">
          <div className="flex items-center gap-3">
            {view === 'list' ? (
              <span className="text-[13px] font-normal text-white/90">Conversations</span>
            ) : (
              <>
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#B3D9FF]/15 border border-[#B3D9FF]/20">
                  <MessageCircle className="h-3.5 w-3.5" fill="#B3D9FF" fillOpacity={0.3} strokeWidth={0} />
                </div>
                <span className="text-[13px] font-normal text-white/90">Fater Support</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            {view === 'chat' && tickets.length > 0 && (
              <button
                onClick={showList}
                className="rounded-lg p-1.5 text-white/30 transition-colors hover:bg-white/5 hover:text-white/60"
                aria-label="View conversations"
                title="All conversations"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={newConversation}
              className="rounded-lg p-1.5 text-white/30 transition-colors hover:bg-white/5 hover:text-white/60"
              aria-label="New conversation"
              title="New conversation"
            >
              <SquarePen className="h-4 w-4" />
            </button>
            <button
              onClick={close}
              className="rounded-lg p-1.5 text-white/30 transition-colors hover:bg-white/5 hover:text-white/60"
              aria-label="Minimize chat"
            >
              <Minus className="h-4 w-4" />
            </button>
            <button
              onClick={close}
              className="rounded-lg p-1.5 text-white/30 transition-colors hover:bg-white/5 hover:text-white/60"
              aria-label="Close chat"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ── LIST VIEW ── */}
        {view === 'list' && (
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {tickets.length === 0 ? (
              <div className="flex h-full items-center justify-center text-[13px] text-white/30">
                No conversations yet
              </div>
            ) : (
              <div className="py-1">
                {tickets.map((ticket) => (
                  <TicketRow
                    key={ticket.id}
                    ticket={ticket}
                    isActive={ticket.id === activeTicketId}
                    onOpen={openTicket}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── CHAT VIEW ── */}
        {view === 'chat' && (
          <>
            {/* Messages area */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 custom-scrollbar">
              {/* Messages are append-only and carry no id (Message is the API payload shape),
                  so the index is a stable identity here: nothing is ever inserted, removed or reordered. */}
              {messages.map((msg, i) => (
                <MessageBubble key={i} message={msg} />
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="rounded-2xl bg-[#242424] px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-white/30 animate-bounce [animation-delay:0ms]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-white/30 animate-bounce [animation-delay:150ms]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-white/30 animate-bounce [animation-delay:300ms]" />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Pending image previews */}
            {pendingImages.length > 0 && (
              <div className="flex gap-2 border-t border-[#2A2A2A] px-3 pt-2 pb-1 overflow-x-auto">
                {pendingImages.map((image) => (
                  <PendingImagePreview key={image.id} image={image} onRemove={removePendingImage} />
                ))}
              </div>
            )}

            {/* Input bar */}
            <div className="border-t border-[#2A2A2A] px-3 py-2.5">
              <form onSubmit={sendMessage} className="flex items-center gap-2">
                <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" multiple className="hidden" />
                <button
                  type="button"
                  onClick={openFilePicker}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-white/30 transition-colors hover:bg-white/5 hover:text-white/60"
                  aria-label="Attach image"
                >
                  <ImagePlus className="h-4 w-4" />
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={handleInputChange}
                  onPaste={handlePaste}
                  placeholder="Message..."
                  className="flex-1 rounded-xl border border-[#2A2A2A] bg-[#141414] px-3.5 py-2 text-[13px] text-white placeholder-white/25 outline-none transition-colors focus:border-[#B3D9FF]/30"
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  disabled={(!input.trim() && pendingImages.length === 0) || isLoading}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#B3D9FF]/15 text-[#B3D9FF] transition-all hover:bg-[#B3D9FF]/25 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Send message"
                >
                  <Send className="h-3.5 w-3.5" />
                </button>
              </form>
            </div>
          </>
        )}
      </div>

      {/* Markdown styles scoped to support chat */}
      <style>{`
        .support-chat-markdown { word-break: break-word; }
        .support-chat-markdown > *:first-child { margin-top: 0; }
        .support-chat-markdown > *:last-child { margin-bottom: 0; }
        .support-chat-markdown p { margin: 0 0 0.35em; }
        .support-chat-markdown p:last-child { margin-bottom: 0; }
        .support-chat-markdown strong { color: rgba(255,255,255,0.9); font-weight: 600; }
        .support-chat-markdown em { color: rgba(255,255,255,0.8); }
        .support-chat-markdown ul, .support-chat-markdown ol { margin: 0.25em 0; padding-left: 1em; }
        .support-chat-markdown li { margin: 0.1em 0; }
        .support-chat-markdown li > p { margin: 0; display: inline; }
        .support-chat-markdown code { background: rgba(255,255,255,0.08); padding: 0.1em 0.3em; border-radius: 4px; font-size: 0.9em; }
        .support-chat-markdown pre { background: rgba(255,255,255,0.06); padding: 0.5em; border-radius: 8px; overflow-x: auto; margin: 0.4em 0; }
        .support-chat-markdown pre code { background: none; padding: 0; }
        .support-chat-markdown a { color: #B3D9FF; text-decoration: underline; }
        .support-chat-markdown h1, .support-chat-markdown h2, .support-chat-markdown h3 { color: rgba(255,255,255,0.9); font-weight: 600; margin: 0.4em 0 0.2em; font-size: 1em; }
        .support-chat-markdown img { max-width: 100%; border-radius: 8px; margin: 0.4em 0; }
        .support-chat-markdown blockquote { margin: 0.3em 0; padding-left: 0.6em; border-left: 2px solid rgba(255,255,255,0.15); }
      `}</style>
    </>
  );
}
